/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Mail, RefreshCw, RotateCcw, XCircle } from "lucide-react";

const STATUS = {
    pending: { label: "Pendiente", badge: "badge-warning" },
    processing: { label: "Procesando", badge: "badge-info" },
    sent: { label: "Enviado", badge: "badge-success" },
    failed: { label: "Fallido", badge: "badge-error" },
    blocked: { label: "Bloqueado", badge: "badge-warning" },
    cancelled: { label: "Cancelado", badge: "badge-ghost" },
};

const EVENT_TYPES = [
    ["", "Todos los eventos"],
    ["order_created_payment_instructions", "Pedido / instrucciones de pago"],
    ["payment_confirmed", "Pago confirmado"],
    ["physical_shipped", "Envío físico"],
    ["digital_available", "Entrega digital"],
    ["order_incident_or_cancelled", "Incidencia o cancelación"],
];

const formatApiError = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    return data.detail || fallback;
};

const formatDate = (value) => value
    ? new Date(value).toLocaleString("es-ES")
    : "Sin dato";

function StatusIcon({ status }) {
    if (status === "sent") return <CheckCircle2 className="w-4 h-4 text-success" aria-hidden="true" />;
    if (status === "failed") return <XCircle className="w-4 h-4 text-error" aria-hidden="true" />;
    return <AlertTriangle className="w-4 h-4 text-warning" aria-hidden="true" />;
}

function EventDetail({ event, busy, onRetry, onCancel }) {
    if (!event) {
        return (
            <aside className="glassCard rounded-xl min-h-80 flex flex-col items-center justify-center gap-2 text-center text-base-content/60 p-6">
                <Mail className="w-10 h-10 opacity-30" aria-hidden="true" />
                <p>Selecciona una comunicación para revisar su historial.</p>
            </aside>
        );
    }

    const status = STATUS[event.status] || { label: event.status, badge: "badge-ghost" };
    const canRetry = ["failed", "blocked", "pending"].includes(event.status);
    const canCancel = ["pending", "failed", "blocked"].includes(event.status);

    return (
        <aside className="glassCard rounded-xl p-4 flex flex-col gap-4 min-h-80">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                    <StatusIcon status={event.status} />
                    <div>
                        <h3 className="font-semibold">{event.event_type_display}</h3>
                        <p className="text-xs text-base-content/55">Pedido {event.order_number}</p>
                    </div>
                </div>
                <span className={`badge badge-sm ${status.badge}`}>{status.label}</span>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><dt className="text-xs text-base-content/50">Destinatario</dt><dd>{event.recipient || "Sin destinatario"}</dd></div>
                <div><dt className="text-xs text-base-content/50">Plantilla</dt><dd>{event.template_version}</dd></div>
                <div><dt className="text-xs text-base-content/50">Creado</dt><dd>{formatDate(event.created_at)}</dd></div>
                <div><dt className="text-xs text-base-content/50">Enviado</dt><dd>{formatDate(event.sent_at)}</dd></div>
                <div><dt className="text-xs text-base-content/50">Intentos</dt><dd>{event.attempt_count}</dd></div>
                <div><dt className="text-xs text-base-content/50">Siguiente intento</dt><dd>{formatDate(event.next_attempt_at)}</dd></div>
            </dl>

            {event.last_error && (
                <div role="alert" className="alert alert-warning text-sm">
                    <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                    <span>{event.last_error}</span>
                </div>
            )}

            <section>
                <h4 className="font-semibold mb-2">Historial de entrega</h4>
                {event.attempts?.length ? (
                    <ol className="flex flex-col gap-2 text-sm">
                        {event.attempts.map((attempt) => (
                            <li key={`${attempt.attempt_number}-${attempt.started_at}`} className="flex items-start gap-2 rounded-lg bg-base-200/60 p-2">
                                <StatusIcon status={attempt.status === "sent" ? "sent" : attempt.status === "failed" ? "failed" : "pending"} />
                                <div>
                                    <p>Intento {attempt.attempt_number}: {attempt.status === "sent" ? "enviado" : attempt.status === "failed" ? "fallido" : "iniciado"}</p>
                                    <p className="text-xs text-base-content/55">{formatDate(attempt.started_at)}{attempt.error_message ? ` · ${attempt.error_message}` : ""}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                ) : (
                    <p className="text-sm text-base-content/55">Todavía no hay intentos de proveedor.</p>
                )}
            </section>

            {(canRetry || canCancel) && (
                <div className="flex flex-wrap justify-end gap-2 pt-1">
                    {canCancel && (
                        <button className="btn btn-sm btn-ghost" onClick={onCancel} disabled={busy}>
                            Cancelar pendiente
                        </button>
                    )}
                    {canRetry && (
                        <button className="btn btn-sm btn-primary gap-2" onClick={onRetry} disabled={busy}>
                            {busy ? <span className="loading loading-spinner loading-xs" /> : <RotateCcw className="w-4 h-4" aria-hidden="true" />}
                            Reintentar
                        </button>
                    )}
                </div>
            )}
        </aside>
    );
}

export default function CommunicationsAdmin() {
    const [events, setEvents] = useState([]);
    const [selected, setSelected] = useState(null);
    const [eventType, setEventType] = useState("");
    const [status, setStatus] = useState("");
    const [order, setOrder] = useState("");
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get("/communications-admin/", {
                params: { event_type: eventType || undefined, status: status || undefined, order: order || undefined },
            });
            setEvents(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            setError(formatApiError(err, "No se pudieron cargar las comunicaciones."));
        } finally {
            setLoading(false);
        }
    }, [eventType, order, status]);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);

    const openEvent = async (id) => {
        setError("");
        try {
            const { data } = await axios.get(`/communications-admin/${id}/`);
            setSelected(data);
        } catch (err) {
            setError(formatApiError(err, "No se pudo cargar el detalle de la comunicación."));
        }
    };

    const runAction = async (action) => {
        if (!selected?.id) return;
        setBusy(true);
        setError("");
        try {
            const { data } = await axios.post(`/communications-admin/${selected.id}/${action}/`);
            setSelected(data);
            await fetchEvents();
        } catch (err) {
            setError(formatApiError(err, "No se pudo actualizar la comunicación."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            <div className="glassCard rounded-xl p-3 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Mail className="w-6 h-6 text-primary" aria-hidden="true" />
                        <div>
                            <h2 className="text-xl font-semibold">Comunicaciones</h2>
                            <p className="text-sm text-base-content/60">Eventos comerciales y entregas de correo</p>
                        </div>
                    </div>
                    <button className="btn btn-sm btn-outline gap-2" onClick={fetchEvents} disabled={loading}>
                        {loading ? <span className="loading loading-spinner loading-xs" /> : <RefreshCw className="w-4 h-4" aria-hidden="true" />}
                        Actualizar
                    </button>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <input
                        className="input input-bordered input-sm"
                        value={order}
                        onChange={(event) => setOrder(event.target.value)}
                        placeholder="Número de pedido"
                        aria-label="Filtrar por número de pedido"
                    />
                    <select className="select select-bordered select-sm" value={eventType} onChange={(event) => setEventType(event.target.value)} aria-label="Filtrar por tipo de evento">
                        {EVENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    <select className="select select-bordered select-sm" value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filtrar por estado">
                        <option value="">Todos los estados</option>
                        {Object.entries(STATUS).map(([value, data]) => <option key={value} value={value}>{data.label}</option>)}
                    </select>
                </div>
            </div>

            {error && (
                <div role="alert" className="alert alert-error shadow-lg">
                    <AlertTriangle className="w-5 h-5" aria-hidden="true" />
                    <span>{error}</span>
                    <button className="btn btn-sm btn-ghost" onClick={fetchEvents}>Reintentar</button>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_520px] gap-3 flex-1 min-h-0">
                <div className="glassCard rounded-xl overflow-auto scrollbar_mod">
                    {loading ? (
                        <div className="flex items-center justify-center h-64"><span className="loading loading-spinner loading-lg text-primary" /></div>
                    ) : events.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 gap-2 text-center text-base-content/60 p-6">
                            <Mail className="w-10 h-10 opacity-30" aria-hidden="true" />
                            <p>No hay comunicaciones con estos filtros.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-zebra">
                                <thead><tr><th>Evento</th><th>Pedido</th><th>Estado</th><th>Intentos</th><th>Fecha</th></tr></thead>
                                <tbody>
                                    {events.map((event) => {
                                        const statusData = STATUS[event.status] || { label: event.status, badge: "badge-ghost" };
                                        return (
                                            <tr key={event.id} className={`cursor-pointer ${selected?.id === event.id ? "bg-primary/10" : ""}`} onClick={() => openEvent(event.id)}>
                                                <td><div className="font-medium">{event.event_type_display}</div><div className="text-xs text-base-content/55">{event.recipient || "Sin destinatario"}</div></td>
                                                <td className="font-mono text-sm">{event.order_number}</td>
                                                <td><span className={`badge badge-sm ${statusData.badge}`}>{statusData.label}</span></td>
                                                <td>{event.attempt_count}</td>
                                                <td className="text-xs text-base-content/60">{formatDate(event.created_at)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <EventDetail
                    event={selected}
                    busy={busy}
                    onRetry={() => runAction("retry")}
                    onCancel={() => runAction("cancel")}
                />
            </div>
        </div>
    );
}
